// 3rd Party
import React, { useEffect, useRef, useState } from 'react';
import { FileRejection } from 'react-dropzone';
import { connect } from 'react-redux';

// Components
import ModalBody from './components/ModalBody';
import SupportSection from './components/SupportSection';
import ModalV2 from '../ModalV2';

// Utils
import { formatMessage } from '@/utils/i18n';
import { MODAL_SIZES } from '@/utils/constants';
import notification from '@/utils/notification';

// Types
import Contact from '@/types/contact';
import { ReduxState } from '@/redux/modules/types';

// Redux
import { loadNewPortfolio } from '@/redux/modules/comparison';
import { togglePortfolioEditor } from '@/redux/modules/common/actionCreators';
import {
  storeMultiPortfolioResults,
  setJobId as setJobIdAction,
  setIsProcessingError,
  processFirstPortfolio,
  resetMultiPortfolio,
} from '@/redux/modules/ai-statement-reader-multi-portfolio';

// Types
import { MultiPortfolioData } from '@/redux/modules/types/ai-statement-reader-multi-portfolio';

// Hooks
import { useAiStatementReaderJob } from './hooks/useAiStatementReaderJob';

type DispatchProps = {
  storeMultiPortfolioResults: (portfolios: MultiPortfolioData[]) => { type: string; portfolios: MultiPortfolioData[] };
  setJobIdAction: (jobId: string | null) => { type: string; jobId: string | null };
  setIsProcessingError: (isProcessingError: boolean) => { type: string; isProcessingError: boolean };
  processFirstPortfolio: () => Promise<MultiPortfolioData | null>;
  resetMultiPortfolio: () => void;
};

type StateProps = {
  currentContact?: Contact;
  portfolios: MultiPortfolioData[];
  isProcessingError: boolean;
};

export type Props = DispatchProps &
  StateProps & {
    onCancel: () => void;
    show: boolean;
    locale: string;
  };

export enum AIStatementReaderSteps {
  UPLOAD,
  PROCESSING,
  EXTRACTING,
}

function MultiPortfolioAiStatementReaderModal({
  show,
  onCancel,
  locale,
  currentContact,
  portfolios,
  isProcessingError,
  storeMultiPortfolioResults,
  setJobIdAction,
  setIsProcessingError,
  processFirstPortfolio,
  resetMultiPortfolio,
}: Props) {
  const i18nBasePath = 'Modal.PortfolioAIStatementReader';
  const API_TIMEOUT_DURATION = 480000; // 8 minutes
  const EXTRACTING_DISPLAY_DURATION = 2000; // 2 seconds

  const [currentStep, setCurrentStep] = useState<AIStatementReaderSteps>(AIStatementReaderSteps.UPLOAD);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [fileRejections, setFileRejections] = useState<FileRejection[]>([]);
  const [hasTimedOut, setHasTimedOut] = useState(false);
  const apiTimeoutRef = useRef<number>();
  const lastSuccessfulJobIdRef = useRef<string | null>(null);

  const { jobId, jobData, startJobWithFile, resetJob } = useAiStatementReaderJob({
    storeMultiPortfolioResults,
    setIsProcessingError,
  });

  const portfolioCount = jobData?.data?.portfolios && Array.isArray(jobData.data.portfolios) ? jobData.data.portfolios.length : 0;

  // Start API timeout when processing begins
  useEffect(() => {
    if (currentStep === AIStatementReaderSteps.PROCESSING && !hasTimedOut) {
      apiTimeoutRef.current = window.setTimeout(() => {
        handleTimeout();
      }, API_TIMEOUT_DURATION);
    }

    return () => {
      clearTimeout(apiTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  // Handle successful portfolios processing
  useEffect(() => {
    // Show extracting state before closing
    // Use ref to track jobId and prevent duplicate notifications when portfolios array reference changes
    if (portfolios?.length && jobId && !hasTimedOut && lastSuccessfulJobIdRef.current !== jobId) {
      lastSuccessfulJobIdRef.current = jobId;

      setCurrentStep(AIStatementReaderSteps.EXTRACTING);

      const extractingDisplayTimeoutId = setTimeout(() => {
        finishProgressAndCloseFlow();
        togglePortfolioEditor(true);
        notification.success(formatMessage(`${i18nBasePath}.SuccessfullyUploadedToast`));
      }, EXTRACTING_DISPLAY_DURATION);

      return () => clearTimeout(extractingDisplayTimeoutId);
    }
  }, [portfolios, jobId, hasTimedOut]);

  // Handle processing errors
  useEffect(() => {
    if (isProcessingError) {
      setCurrentStep(AIStatementReaderSteps.UPLOAD);
    }
  }, [isProcessingError]);

  const closeFlow = () => {
    lastSuccessfulJobIdRef.current = null;
    setUploadedFiles([]);
    setFileRejections([]);
    setHasTimedOut(false);
    clearTimeout(apiTimeoutRef.current);
    resetJob();
    setCurrentStep(AIStatementReaderSteps.UPLOAD);
    setIsProcessingError(false);
    processFirstPortfolio();
    onCancel();
  };

  const finishProgressAndCloseFlow = () => {
    clearTimeout(apiTimeoutRef.current);
    setTimeout(() => closeFlow(), 300);
  };

  const processFiles = async () => {
    setCurrentStep(AIStatementReaderSteps.PROCESSING);
    setHasTimedOut(false);
    loadNewPortfolio(currentContact);

    // Currently only supporting single file upload
    const resultJobId = await startJobWithFile(uploadedFiles[0]);
    if (resultJobId) {
      setJobIdAction(resultJobId);
    }
  };

  const handleDrop = (newUploadedFiles: File[], newFileRejections: FileRejection[]) => {
    setUploadedFiles([...uploadedFiles, ...newUploadedFiles]);
    setFileRejections([...fileRejections, ...newFileRejections]);
  };

  const handleRemoveFile = (file: File) => {
    setUploadedFiles(uploadedFiles.filter(uploadedFile => uploadedFile !== file));
    setFileRejections(fileRejections.filter(fileRejection => fileRejection.file !== file));

    // Reset to upload step and clear error states when removing files
    setCurrentStep(AIStatementReaderSteps.UPLOAD);
    setIsProcessingError(false);
    setHasTimedOut(false);
    resetJob();
  };

  const getFooterButtons = () => {
    if (currentStep === AIStatementReaderSteps.UPLOAD) {
      return {
        cancelLabelToLocalize: 'Common.Cancel',
        cancelOnClick: closeFlow,
        primaryLabelToLocalize: 'Common.Continue',
        primaryOnClick: processFiles,
        primaryDisabled: !uploadedFiles.length || !!fileRejections.length,
      };
    }
    return {};
  };

  const handleTimeout = () => {
    // Mark the flow as timed out so any in‑flight success handlers are ignored
    setHasTimedOut(true);
    // Cancel the job polling to prevent further API calls
    resetJob();
    // Clear any multi-portfolio data from the previous attempt
    resetMultiPortfolio();
  };

  return (
    <ModalV2
      show={show}
      title={`${i18nBasePath}.Title`}
      modalClassName="multi-portfolio-ai-statement-reader-modal"
      body={
        <ModalBody
          currentStep={currentStep}
          uploadedFiles={uploadedFiles}
          fileRejections={fileRejections}
          isProcessingError={isProcessingError}
          jobData={jobData}
          hasTimedOut={hasTimedOut}
          portfolioCount={portfolioCount}
          onDrop={handleDrop}
          onRemove={handleRemoveFile}
        />
      }
      onClose={() => closeFlow()}
      size={MODAL_SIZES.LG}
      defaultFooterButtons={getFooterButtons()}
      support={<SupportSection locale={locale} />}
    />
  );
}

const mapStateToProps = (state: ReduxState): StateProps => ({
  currentContact: state.contacts.current?.contact,
  portfolios: state.aiStatementReaderMultiPortfolio.portfolios,
  isProcessingError: state.aiStatementReaderMultiPortfolio.isProcessingError,
});

export default connect(mapStateToProps, {
  storeMultiPortfolioResults,
  setJobIdAction,
  setIsProcessingError,
  processFirstPortfolio,
  resetMultiPortfolio,
})(MultiPortfolioAiStatementReaderModal);
